'use client';

import { useEffect, useState } from 'react';
import { useInView } from 'react-intersection-observer';

import DescriptionContent from './DescriptionContent';
import MustKnowContent from './MustKnowContent';
import RoomsContent from './RoomsContent';

import { IVillageDictionary } from '@/dictionaries/pages/village/types';
import { VillageRooms, VillageTranslation } from '@/services/types';
import { cn } from '@/lib/utils';
import useObserverContext from '@/hooks/useObserverContext';
import Slider, { Settings } from 'react-slick';

type Props = {
  translations: VillageTranslation;
  villageDict: IVillageDictionary;
  rooms: VillageRooms[];
  villageId: number;
  lang: string;
};

export default function VillageDescriptionTabs({
  translations,
  villageDict,
  rooms: villageRooms,
  villageId,
  lang,
}: Props) {
  const { description, rooms, mustKnow } = villageDict.descriptionSections;
  const [selectedSection, setSelectedSection] = useState(description);
  const { handleCurrentSection } = useObserverContext();
  const { ref, inView } = useInView();

  const sections = [description, rooms, mustKnow];

  useEffect(() => {
    if (inView) {
      handleCurrentSection('description');
    } else {
      handleCurrentSection('');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inView]);

  const topDetailSlide: Settings = {
    responsive: [
      {
        breakpoint: 9999,
        settings: 'unslick',
      },
      {
        breakpoint: 480,
        settings: {
          centerMode: true,
          focusOnSelect: true,
          slidesToShow: 1,
          arrows: false,
          dots: false,
          infinite: false,
          variableWidth: true,
        },
      },
      {
        breakpoint: 600,
        settings: {
          centerMode: true,
          slidesToShow: 2,
          arrows: false,
          dots: false,
          infinite: false,
          variableWidth: true,
        },
      },
      {
        breakpoint: 1024,
        settings: {
          centerMode: false,
          slidesToShow: 3,
          arrows: false,
          dots: false,
          infinite: false,
          variableWidth: true,
        },
      },
    ],
  };

  return (
    <div className="village-description-tabs">
      <div ref={ref}>
        <Slider
          {...topDetailSlide}
          className="mx-[-0.9375rem] border border-white bg-white list-none flex flex-nowrap">
          {sections.map((section, index) => (
            <div
              onClick={() => setSelectedSection(section)}
              role="presentation"
              key={section}
              className="float-left">
              <div
                className={cn(
                  'bg-white text-graphite block py-5 px-6 text-xs leading-[1] cursor-pointer',
                  {
                    'pl-0 md:pl-4': index == 0,
                  },
                )}>
                <h3
                  className={cn(
                    'text-gray-500 m-0 text-center font-title text-4xl leading-[1.25] bg-gradient-to-b bg-no-repeat bg-[length:95%_0.2em] bg-[50%_75%] hover:text-graphite hover:from-underline hover:to-underline',
                    {
                      'px-[0.2em] mx-[-0.2em] text-graphite from-underline to-underline':
                        selectedSection === section,
                    },
                  )}>
                  {section}
                </h3>
              </div>
            </div>
          ))}
        </Slider>
      </div>
      <div className="border border-white bg-white text-graphite transition-all duration-500 ease-in">
        {selectedSection === description && (
          <DescriptionContent
            villageDict={villageDict}
            translations={translations}
          />
        )}
        {selectedSection === mustKnow && (
          <MustKnowContent
            villageDict={villageDict}
            translations={translations}
          />
        )}
        {selectedSection === rooms && (
          <RoomsContent
            rooms={villageRooms}
            villageDict={villageDict}
            villageId={villageId}
            lang={lang}
          />
        )}
      </div>
    </div>
  );
}
